import React from 'react';
import {
  groupAffectedItemsByLocation,
  groupClinicalFinalizeIssues,
  type ClinicalFinalizeIssueLike,
} from '@/utils/clinical-finalize-warnings';

type ClinicalWarningsSummaryProps = {
  issues: ClinicalFinalizeIssueLike[];
  className?: string;
  tone?: 'warning' | 'blocked';
};

const ClinicalWarningsSummary: React.FC<ClinicalWarningsSummaryProps> = ({
  issues,
  className,
  tone = 'warning',
}) => {
  const groups = React.useMemo(() => groupClinicalFinalizeIssues(issues), [issues]);
  const styles = tone === 'blocked'
    ? {
        section: 'border-red-200 bg-red-50/70',
        heading: 'text-red-950',
        accent: 'border-red-400',
        location: 'text-red-800',
      }
    : {
        section: 'border-amber-200 bg-amber-50/70',
        heading: 'text-amber-950',
        accent: 'border-amber-400',
        location: 'text-amber-800',
      };

  if (groups.length === 0) return null;

  return (
    <div className={className}>
      <div className="space-y-3">
        {groups.map((group) => (
          <section key={group.key} className={`rounded-lg border p-3 ${styles.section}`}>
            <h4 className={`text-sm font-semibold ${styles.heading}`}>{group.title}</h4>
            <div className="mt-2 space-y-2.5">
              {group.warnings.map((warning) => {
                const affectedLocations = groupAffectedItemsByLocation(warning);
                return (
                  <div key={warning.key} className={`border-l-2 pl-2.5 ${styles.accent}`}>
                    {affectedLocations.length > 0 ? (
                      <>
                        <p className="text-sm font-medium leading-5 text-slate-700">{warning.message}</p>
                        <div className="mt-1 space-y-1">
                          {affectedLocations.map(({ location, itemNames }) => (
                            <p key={location} className="text-sm leading-5 text-slate-700">
                              <span className={`text-xs font-semibold ${styles.location}`}>{location}:</span>{' '}
                              {itemNames.join(', ')}
                            </p>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <p className={`text-xs font-medium leading-5 ${styles.location}`}>
                          {warning.locations.join(' • ')}
                        </p>
                        <p className="mt-0.5 text-sm leading-5 text-slate-700">{warning.message}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ClinicalWarningsSummary;
